import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";
import { Reveal, Eyebrow, EASE } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const UeberUns = () => {
  const { t } = useLanguage();
  return (
    <section id="ueber-uns" className="bg-dark py-20 lg:py-32">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-16">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>{t.about.eyebrow}</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-4 font-display text-4xl font-medium tracking-tight sm:text-5xl">{t.about.title}</h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-inkmuted">{t.about.lead}</p>
            </Reveal>

            <Reveal delay={0.22}>
              <p className="mt-12 font-mono text-xs uppercase tracking-[0.3em] text-bone/40">{t.about.label}</p>
            </Reveal>
            <div className="mt-5 space-y-4">
              {t.about.people.map((p, i) => (
                <Reveal key={p.name} delay={0.26 + i * 0.08}>
                  <div
                    data-testid={`about-person-${i + 1}`}
                    className="flex items-center gap-4 rounded-[3px] border border-bone/10 bg-carddark p-5 transition-colors duration-300 hover:border-gold"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/40 bg-goldsoft font-display text-lg font-bold text-gold">
                      {p.name.charAt(0)}
                    </span>
                    <div>
                      <p className="font-display text-base font-semibold tracking-tight">{p.name}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">{p.role}</p>
                      <p className="mt-1 text-[13px] text-inkmuted">{p.line}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2">
              {t.about.principles.map((p, i) => (
                <Reveal key={p.k} delay={0.1 + i * 0.08}>
                  <div
                    data-testid={`about-principle-${i + 1}`}
                    className="group h-full border-t border-bone/10 py-8 sm:px-8 sm:first:pl-0 lg:first:pl-0"
                  >
                    <span className="font-mono text-xs tracking-[0.25em] text-gold">[ {String(i + 1).padStart(2, "0")} ]</span>
                    <h3 className="mt-4 font-display text-xl font-medium tracking-tight transition-colors duration-300 group-hover:text-gold">
                      {p.k}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-inkmuted">{p.copy}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <motion.button
                data-testid="about-cta"
                onClick={() => scrollToId("kontakt")}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.3, ease: EASE }}
                className="group mt-10 inline-flex items-center gap-3 rounded-[3px] bg-gold px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone"
              >
                {t.about.cta}
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
              </motion.button>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-12">
            <div data-testid="about-reference" className="grid grid-cols-1 items-center gap-8 overflow-hidden rounded-[3px] border border-bone/10 bg-carddark lg:grid-cols-12">
              <div className="relative h-56 overflow-hidden lg:col-span-5 lg:h-64">
                <img src="/referenz-dickies-helden.jpg" alt={t.about.refTitle} loading="lazy" style={{ objectPosition: "50% 30%" }} className="h-full w-full object-cover" />
              </div>
              <div className="p-8 lg:col-span-7 lg:p-12">
                <Eyebrow>{t.about.refBadge}</Eyebrow>
                <h3 className="mt-3 font-display text-2xl font-medium tracking-tight sm:text-3xl">{t.about.refTitle}</h3>
                <p className="mt-3 text-sm leading-relaxed text-inkmuted">{t.about.refCopy}</p>
                <a
                  data-testid="about-reference-link"
                  href="https://dickies-helden.de"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group mt-6 inline-flex items-center gap-2 rounded-[3px] bg-gold px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone"
                >
                  {t.about.refLink}
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default UeberUns;
